// ─── src/data/italy/cities.js ──────────────────────────────────────────────────
// Italy city entities, only under primary regions.
// Cities only exist if the parent region exists.
// Schema: id, regionSlug, countrySlug, slug, name, listingCount,
//         lat, lng, canonicalRoute, description, focusKeywords, aiSummary,
//         entityAliases.
//
// Listings with city values not in this list preserve the value as legacyCityName.

export const ITALY_CITIES = [
  // ── Amalfi Coast ─────────────────────────────────────────────────────────
  {
    id: "positano", regionSlug: "amalfi-coast", countrySlug: "italy", slug: "positano", name: "Positano",
    listingCount: 2, lat: "40.6280", lng: "14.4850",
    canonicalRoute: "/italy/amalfi-coast/positano",
    description: "Pastel cascading village on the cliffside. Iconic Mediterranean wedding backdrop.",
    focusKeywords: ["positano wedding", "positano wedding venue"],
    aiSummary: "Strong visual brand. Instagram-driven demand. Beach ceremony niche.",
    entityAliases: [],
  },
  {
    id: "ravello", regionSlug: "amalfi-coast", countrySlug: "italy", slug: "ravello", name: "Ravello",
    listingCount: 3, lat: "40.6490", lng: "14.6117",
    canonicalRoute: "/italy/amalfi-coast/ravello",
    description: "Clifftop gardens and panoramic terraces above the Mediterranean. Home to Villa Cimbrone and Villa Rufolo.",
    focusKeywords: ["ravello wedding", "ravello wedding venue"],
    aiSummary: "Highest-intent wedding city on the Amalfi Coast. Villa Cimbrone dominates search.",
    entityAliases: [],
  },
  {
    id: "praiano", regionSlug: "amalfi-coast", countrySlug: "italy", slug: "praiano", name: "Praiano",
    listingCount: 0, lat: "40.6134", lng: "14.5281",
    canonicalRoute: "/italy/amalfi-coast/praiano",
    description: "A quieter, more intimate Amalfi Coast village between Positano and Amalfi with dramatic sunset views.",
    focusKeywords: ["praiano wedding", "praiano wedding venue"],
    aiSummary: "Intimate alternative to Positano. Sunset-facing. Growing boutique market.",
    entityAliases: [],
  },
  {
    id: "sorrento", regionSlug: "amalfi-coast", countrySlug: "italy", slug: "sorrento", name: "Sorrento",
    listingCount: 0, lat: "40.6263", lng: "14.3758",
    canonicalRoute: "/italy/amalfi-coast/sorrento",
    description: "Clifftop town overlooking the Bay of Naples, gateway to the Amalfi Coast with its own grand hotel tradition.",
    focusKeywords: ["sorrento wedding", "sorrento wedding venue"],
    aiSummary: "Gateway city. Grand hotel circuit. Strong package holiday crossover. More accessible price point.",
    entityAliases: [],
  },
  {
    id: "amalfi-town", regionSlug: "amalfi-coast", countrySlug: "italy", slug: "amalfi", name: "Amalfi",
    listingCount: 1, lat: "40.6340", lng: "14.6027",
    canonicalRoute: "/italy/amalfi-coast/amalfi",
    description: "Historic maritime republic with the stunning Duomo di Amalfi as centrepiece.",
    focusKeywords: ["amalfi wedding"],
    aiSummary: "Cathedral ceremonies drive search. Lower volume than Ravello/Positano.",
    entityAliases: [],
  },
  {
    id: "conca-dei-marini", regionSlug: "amalfi-coast", countrySlug: "italy", slug: "conca-dei-marini", name: "Conca dei Marini",
    listingCount: 0, lat: "40.6197", lng: "14.5744",
    canonicalRoute: "/italy/amalfi-coast/conca-dei-marini",
    description: "Tiny hidden gem between Amalfi and Positano, home to the Grotta dello Smeraldo and select ultra-luxury hotels.",
    focusKeywords: ["conca dei marini wedding"],
    aiSummary: "Ultra-exclusive micro-village. Monastero Santa Rosa effect. Very intimate.",
    entityAliases: [],
  },

  // ── Tuscany ──────────────────────────────────────────────────────────────
  {
    id: "florence", regionSlug: "tuscany", countrySlug: "italy", slug: "florence", name: "Florence",
    listingCount: 3, lat: "43.7696", lng: "11.2558",
    canonicalRoute: "/italy/tuscany/florence",
    description: "Renaissance capital, palazzi, Duomo views and world-class art as wedding backdrop.",
    focusKeywords: ["florence wedding", "florence wedding venue"],
    aiSummary: "Urban luxury hub within Tuscany. Palazzo and boutique hotel venues lead.",
    entityAliases: ["Firenze"],
  },
  {
    id: "chianti", regionSlug: "tuscany", countrySlug: "italy", slug: "chianti", name: "Chianti",
    listingCount: 3, lat: "43.4592", lng: "11.3075",
    canonicalRoute: "/italy/tuscany/chianti",
    description: "Rolling vineyards and medieval estates in the heart of Tuscany's wine country.",
    focusKeywords: ["chianti wedding", "vineyard wedding tuscany"],
    aiSummary: "Quintessential villa/vineyard market. Strongest rustic-luxe positioning.",
    entityAliases: ["Chianti Classico"],
  },
  {
    id: "siena", regionSlug: "tuscany", countrySlug: "italy", slug: "siena", name: "Siena",
    listingCount: 1, lat: "43.3188", lng: "11.3307",
    canonicalRoute: "/italy/tuscany/siena",
    description: "Medieval jewel, the Piazza del Campo, Gothic cathedral and contrada culture at its most theatrical.",
    focusKeywords: ["siena wedding", "siena wedding venue"],
    aiSummary: "Medieval city grandeur. Strong photography backdrop. Day-trip proximity to Chianti vineyards.",
    entityAliases: [],
  },
  {
    id: "val-d-orcia", regionSlug: "tuscany", countrySlug: "italy", slug: "val-d-orcia", name: "Val d'Orcia",
    listingCount: 1, lat: "43.0560", lng: "11.4892",
    canonicalRoute: "/italy/tuscany/val-d-orcia",
    description: "UNESCO landscape of cypress-lined roads, Brunello vineyards and golden hilltop hamlets.",
    focusKeywords: ["val d'orcia wedding", "montalcino wedding", "brunello wedding"],
    aiSummary: "Most photographed Tuscan landscape. Brunello wine estates. Pienza and Montalcino anchor.",
    entityAliases: ["Val d'Orcia", "Valdorcia"],
  },
  {
    id: "lucca", regionSlug: "tuscany", countrySlug: "italy", slug: "lucca", name: "Lucca",
    listingCount: 0, lat: "43.8430", lng: "10.5027",
    canonicalRoute: "/italy/tuscany/lucca",
    description: "Renaissance city walls, Puccini's birthplace and the nearby Lucchesia hills dotted with aristocratic villas.",
    focusKeywords: ["lucca wedding", "lucca wedding venue"],
    aiSummary: "Walled city charm. Villa circuit in Lucchesia hills. Growing alternative to Florence.",
    entityAliases: [],
  },
  {
    id: "montepulciano", regionSlug: "tuscany", countrySlug: "italy", slug: "montepulciano", name: "Montepulciano",
    listingCount: 1, lat: "43.0998", lng: "11.7854",
    canonicalRoute: "/italy/tuscany/montepulciano",
    description: "Hilltop town of Vino Nobile, Renaissance palazzi, Piazza Grande and panoramic Valdichiana views.",
    focusKeywords: ["montepulciano wedding", "montepulciano wedding venue"],
    aiSummary: "Wine estate market. Nobile di Montepulciano association. Strong foodie appeal.",
    entityAliases: [],
  },

  // ── Lake Como ────────────────────────────────────────────────────────────
  {
    id: "bellagio", regionSlug: "lake-como", countrySlug: "italy", slug: "bellagio", name: "Bellagio",
    listingCount: 2, lat: "45.9862", lng: "9.2611",
    canonicalRoute: "/italy/lake-como/bellagio",
    description: "The Pearl of Lake Como, where the lake's three branches meet in spectacular fashion.",
    focusKeywords: ["bellagio wedding", "bellagio lake como wedding"],
    aiSummary: "Iconic location. Grand Hotel Villa Serbelloni anchors demand.",
    entityAliases: [],
  },
  {
    id: "como", regionSlug: "lake-como", countrySlug: "italy", slug: "como", name: "Como",
    listingCount: 0, lat: "45.8081", lng: "9.0852",
    canonicalRoute: "/italy/lake-como/como",
    description: "The lake's southern city, silk heritage, the Duomo and a growing luxury boutique hotel scene.",
    focusKeywords: ["como wedding", "como city wedding venue"],
    aiSummary: "Urban base for lake weddings. Growing hotel market. Cathedral ceremonies.",
    entityAliases: [],
  },
  {
    id: "cernobbio", regionSlug: "lake-como", countrySlug: "italy", slug: "cernobbio", name: "Cernobbio",
    listingCount: 2, lat: "45.8401", lng: "9.0697",
    canonicalRoute: "/italy/lake-como/cernobbio",
    description: "Home to Villa d'Este, the most celebrated hotel on Lake Como and one of the world's finest wedding settings.",
    focusKeywords: ["cernobbio wedding", "villa d'este wedding"],
    aiSummary: "Villa d'Este single-handedly dominates. Ultra-premium. Conference-luxury crossover.",
    entityAliases: [],
  },
  {
    id: "tramezzo", regionSlug: "lake-como", countrySlug: "italy", slug: "tramezzo", name: "Tremezzo",
    listingCount: 0, lat: "45.9867", lng: "9.2206",
    canonicalRoute: "/italy/lake-como/tramezzo",
    description: "Grand Hotel Tremezzo and Villa Carlotta, the western shore's most glamorous stretch.",
    focusKeywords: ["tremezzo wedding", "grand hotel tremezzo wedding"],
    aiSummary: "Grand Hotel Tremezzo anchor. Villa Carlotta gardens. Western shore luxury strip.",
    entityAliases: ["Tremezzina"],
  },
  {
    id: "menaggio", regionSlug: "lake-como", countrySlug: "italy", slug: "menaggio", name: "Menaggio",
    listingCount: 0, lat: "46.0207", lng: "9.2369",
    canonicalRoute: "/italy/lake-como/menaggio",
    description: "Central western shore town with mountain views and ferry access to Bellagio and Varenna.",
    focusKeywords: ["menaggio wedding"],
    aiSummary: "Mid-lake hub. Ferry crossover point. Growing boutique market.",
    entityAliases: [],
  },
  {
    id: "varenna", regionSlug: "lake-como", countrySlug: "italy", slug: "varenna", name: "Varenna",
    listingCount: 1, lat: "46.0102", lng: "9.2841",
    canonicalRoute: "/italy/lake-como/varenna",
    description: "Quiet east-shore village with pastel waterfront and botanical gardens.",
    focusKeywords: ["varenna wedding"],
    aiSummary: "Intimate alternative to west shore. Growing.",
    entityAliases: [],
  },

  // ── Sicily ───────────────────────────────────────────────────────────────
  {
    id: "taormina", regionSlug: "sicily", countrySlug: "italy", slug: "taormina", name: "Taormina",
    listingCount: 1, lat: "37.8515", lng: "15.2866",
    canonicalRoute: "/italy/sicily/taormina",
    description: "Ancient Greek theatre above the Ionian Sea with Mount Etna as backdrop, Sicily's crown jewel.",
    focusKeywords: ["taormina wedding", "taormina wedding venue"],
    aiSummary: "Sicily's luxury anchor. Belmond Grand Hotel Timeo effect. Etna backdrop unique.",
    entityAliases: [],
  },
  {
    id: "palermo", regionSlug: "sicily", countrySlug: "italy", slug: "palermo", name: "Palermo",
    listingCount: 1, lat: "38.1234", lng: "13.3765",
    canonicalRoute: "/italy/sicily/palermo",
    description: "Sicily's capital, Norman palaces, baroque churches, street food culture and theatrical grandeur.",
    focusKeywords: ["palermo wedding", "palermo wedding venue"],
    aiSummary: "Urban cultural hub. Palazzo and garden venues. Strong local food culture.",
    entityAliases: [],
  },
  {
    id: "cefalu", regionSlug: "sicily", countrySlug: "italy", slug: "cefalu", name: "Cefalù",
    listingCount: 0, lat: "38.0386", lng: "14.0233",
    canonicalRoute: "/italy/sicily/cefalu",
    description: "Norman cathedral, golden beach and a dramatic rocky promontory on Sicily's northern coast.",
    focusKeywords: ["cefalu wedding"],
    aiSummary: "Beach-meets-medieval niche. Club Med Cefalù adds resort option. Photogenic.",
    entityAliases: ["Cefalù"],
  },
  {
    id: "noto", regionSlug: "sicily", countrySlug: "italy", slug: "noto", name: "Noto",
    listingCount: 0, lat: "36.8914", lng: "15.0701",
    canonicalRoute: "/italy/sicily/noto",
    description: "UNESCO baroque masterpiece rebuilt after the 1693 earthquake, honey-coloured limestone perfection.",
    focusKeywords: ["noto wedding", "noto wedding venue"],
    aiSummary: "Baroque architecture unique draw. Growing luxury boutique hotel scene. Photography paradise.",
    entityAliases: [],
  },
  {
    id: "syracuse", regionSlug: "sicily", countrySlug: "italy", slug: "syracuse", name: "Syracuse",
    listingCount: 0, lat: "37.0755", lng: "15.2866",
    canonicalRoute: "/italy/sicily/syracuse",
    description: "Ancient Greek ruins, Ortigia island and 2,800 years of continuous civilisation on the Ionian coast.",
    focusKeywords: ["syracuse wedding", "ortigia wedding"],
    aiSummary: "Ortigia island ceremonies. Ancient Greek theatre backdrop. Emerging luxury.",
    entityAliases: ["Siracusa", "Ortigia"],
  },

  // ── Puglia ───────────────────────────────────────────────────────────────
  {
    id: "ostuni", regionSlug: "puglia", countrySlug: "italy", slug: "ostuni", name: "Ostuni",
    listingCount: 0, lat: "40.7295", lng: "17.5778",
    canonicalRoute: "/italy/puglia/ostuni",
    description: "The White City, hilltop old town cascading in whitewashed stone above ancient olive groves.",
    focusKeywords: ["ostuni wedding", "ostuni wedding venue"],
    aiSummary: "Instagrammable White City. Masseria circuit surrounds. Strong visual brand.",
    entityAliases: ["La Città Bianca"],
  },
  {
    id: "lecce", regionSlug: "puglia", countrySlug: "italy", slug: "lecce", name: "Lecce",
    listingCount: 0, lat: "40.3516", lng: "18.1750",
    canonicalRoute: "/italy/puglia/lecce",
    description: "The Florence of the South, extravagant Lecce baroque carved in golden pietra leccese stone.",
    focusKeywords: ["lecce wedding", "lecce wedding venue"],
    aiSummary: "Baroque urban grandeur. Palazzo venues. Growing international awareness.",
    entityAliases: ["Firenze del Sud"],
  },
  {
    id: "polignano-a-mare", regionSlug: "puglia", countrySlug: "italy", slug: "polignano-a-mare", name: "Polignano a Mare",
    listingCount: 0, lat: "40.9944", lng: "17.2167",
    canonicalRoute: "/italy/puglia/polignano-a-mare",
    description: "Dramatic clifftop town above turquoise Adriatic waters, Domenico Modugno's birthplace and beach-grotto dining.",
    focusKeywords: ["polignano a mare wedding"],
    aiSummary: "Cliff dining and grotto ceremony niche. Strong visual brand. Grotta Palazzese effect.",
    entityAliases: ["Polignano"],
  },
  {
    id: "fasano", regionSlug: "puglia", countrySlug: "italy", slug: "fasano", name: "Fasano",
    listingCount: 1, lat: "40.8359", lng: "17.3615",
    canonicalRoute: "/italy/puglia/fasano",
    description: "Masseria heartland, ancient fortified farmhouses set among olive groves in the Valle d'Itria.",
    focusKeywords: ["fasano wedding", "masseria wedding fasano"],
    aiSummary: "Masseria concentration highest here. Borgo Egnazia and Torre Coccaro anchor. Trulli landscape.",
    entityAliases: [],
  },
  {
    id: "martina-franca", regionSlug: "puglia", countrySlug: "italy", slug: "martina-franca", name: "Martina Franca",
    listingCount: 0, lat: "40.7014", lng: "17.3386",
    canonicalRoute: "/italy/puglia/martina-franca",
    description: "Baroque hilltop town in the Valle d'Itria, trulli-dotted countryside and summer opera festival.",
    focusKeywords: ["martina franca wedding"],
    aiSummary: "Trulli landscape. Opera festival cultural draw. Boutique market.",
    entityAliases: [],
  },

  // ── Italian Riviera ──────────────────────────────────────────────────────
  {
    id: "portofino", regionSlug: "italian-riviera", countrySlug: "italy", slug: "portofino", name: "Portofino",
    listingCount: 0, lat: "44.3033", lng: "9.2091",
    canonicalRoute: "/italy/italian-riviera/portofino",
    description: "The most photographed harbour in the Mediterranean, pastel facades, superyachts and Ligurian glamour.",
    focusKeywords: ["portofino wedding", "portofino wedding venue"],
    aiSummary: "Ultra-luxury micro-village. Splendido Hotel anchor. Celebrity association. Extremely limited capacity.",
    entityAliases: [],
  },
  {
    id: "cinque-terre", regionSlug: "italian-riviera", countrySlug: "italy", slug: "cinque-terre", name: "Cinque Terre",
    listingCount: 0, lat: "44.1461", lng: "9.6437",
    canonicalRoute: "/italy/italian-riviera/cinque-terre",
    description: "Five UNESCO fishing villages clinging to the Ligurian cliff coast, terraced vineyards and turquoise coves.",
    focusKeywords: ["cinque terre wedding", "cinque terre wedding venue"],
    aiSummary: "Aspirational search volume high. Limited venue capacity. Elopement and intimate ceremony niche.",
    entityAliases: ["5 Terre"],
  },

  // ── Dolomites ────────────────────────────────────────────────────────────
  {
    id: "cortina-d-ampezzo", regionSlug: "dolomites", countrySlug: "italy", slug: "cortina-d-ampezzo", name: "Cortina d'Ampezzo",
    listingCount: 0, lat: "46.5404", lng: "12.1357",
    canonicalRoute: "/italy/dolomites/cortina-d-ampezzo",
    description: "Queen of the Dolomites, Olympic ski resort, Cristallo Hotel and dramatic mountain-peak ceremonies.",
    focusKeywords: ["cortina wedding", "cortina d'ampezzo wedding venue"],
    aiSummary: "Alpine luxury anchor. Winter and summer dual season. 2026 Olympics will boost profile.",
    entityAliases: ["Cortina"],
  },
  {
    id: "val-gardena", regionSlug: "dolomites", countrySlug: "italy", slug: "val-gardena", name: "Val Gardena",
    listingCount: 0, lat: "46.5583", lng: "11.6756",
    canonicalRoute: "/italy/dolomites/val-gardena",
    description: "South Tyrolean valley of Ladin culture, wood-carving tradition and spectacular Dolomite panoramas.",
    focusKeywords: ["val gardena wedding"],
    aiSummary: "Ladin cultural niche. Ski resort infrastructure. Mountain ceremony settings.",
    entityAliases: ["Gröden"],
  },

  // ── Sardinia ─────────────────────────────────────────────────────────────
  {
    id: "costa-smeralda", regionSlug: "sardinia", countrySlug: "italy", slug: "costa-smeralda", name: "Costa Smeralda",
    listingCount: 0, lat: "41.0861", lng: "9.5092",
    canonicalRoute: "/italy/sardinia/costa-smeralda",
    description: "Emerald waters, Porto Cervo glamour and the Aga Khan's legendary resort coastline.",
    focusKeywords: ["costa smeralda wedding", "costa smeralda wedding venue"],
    aiSummary: "Ultra-luxury resort strip. Superyacht culture. Cala di Volpe and Pitrizza anchor.",
    entityAliases: [],
  },
  {
    id: "porto-cervo", regionSlug: "sardinia", countrySlug: "italy", slug: "porto-cervo", name: "Porto Cervo",
    listingCount: 0, lat: "41.0919", lng: "9.5317",
    canonicalRoute: "/italy/sardinia/porto-cervo",
    description: "The capital of Costa Smeralda, designer boutiques, marina superyachts and exclusive beach clubs.",
    focusKeywords: ["porto cervo wedding"],
    aiSummary: "Marina-centred celebrations. Beach club receptions. Highest per-night spend in Sardinia.",
    entityAliases: [],
  },
];
